import { assign } from "lodash-es";
import $ from "jquery";
import shared from "./_shared";

const debug = payload => {
    if (console.debug) {
        console.debug(payload);
    }
};

// VIDEO
const videoOptions = assign({}, shared.videoJSOptions, {
    controlBar: {
        children: ["progressControl", "currentTimeDisplay", "timeDivider", "durationDisplay"],
    },
    ControlBar: {
        children: ["muteToggle", "captionsButton"],
    },
});

const videoResize = player => {
    const $player = $(player.el());
    if (!$player.hasClass("kb-vjs-ignore")) {
        const $controlBars = $player.find(".vjs-control-bar").removeAttr("style");
        const $bigPlayButton = $player.find(".vjs-big-play-button").removeAttr("style");
        const playerWidth = $player.width();
        const breakPoint = shared.playerBreakpoints.L.size;

        if (playerWidth > breakPoint) {
            const multiplier = playerWidth / breakPoint;
            const transform = `scale(${multiplier})`;
            $controlBars.first().css({ transform });
            $controlBars.last().css({
                transform,
                "transform-origin": "top right",
            });
            $bigPlayButton.css({
                transform: `${transform} translateX(-50%) translateY(-50%)`,
            });
        }
    }
};

export default async ({
    videojs,
    container,
    el,
    isEdit,
    fromFroala,
    disableFullScreen,
    scorm,
    onFullscreenChange,
    filestack,
    isExport,
    getDynamicMediaData,
    getAssetA11y,
    onProgress,
    // eslint-disable-next-line
    getFlags,
}) => {
    const $el = $(el);
    if (Object.prototype.hasOwnProperty.call(shared.videoStack, $el.attr("data-v-id"))) {
        return;
    }

    const media = shared._vjsEncode($el.data("media"), true);
    if (media.id && (getDynamicMediaData || (getDynamicMediaData && !isExport))) {
        const { poster } = await getDynamicMediaData(media.id).catch(e => {
            debug(e);
            return media;
        });
        media.poster = poster;
    }

    if (media.id && (getAssetA11y || (getAssetA11y && !isExport))) {
        const { a11y } = await getAssetA11y(media.id, "Media").catch(e => {
            debug(e);
            return media;
        });
        media.a11y = a11y;
    }

    const provider = media.provider;
    const url = media.url;
    const poster = media.poster;
    const a11y = media.a11y;
    const id = `video_${Math.floor(Math.random() * 1000 + 1)}`;
    $el.attr("data-v-id", id);
    $el.attr("id", id);

    const settings = shared._getSettings($el, videoOptions);

    let options = settings.options;
    const controlBar = options.ControlBar || options.controlBar;

    if (!controlBar.children.includes("fullscreenToggle") && !scorm) {
        controlBar.children.push("fullscreenToggle");
    }

    if (settings.fullscreenDisabled || disableFullScreen) {
        controlBar.children.splice(controlBar.children.indexOf("fullscreenToggle"), 1);
    }

    if (settings.inactivityDisabled) {
        options.inactivityTimeout = 0;
    }

    if (provider === "upload") {
        options.sources = [{ src: url, type: media.type }];
    } else if (
        provider === "external" ||
        provider === "kaltura" ||
        provider === "brightcove" ||
        provider === "ooyala"
    ) {
        const splitPathRe = /^(\/?)([\s\S]*?)((?:\.{1,2}|[^/]+?)(\.([^./?]+)))(?:[/]*|[?].*)$/i;
        const pathParts = splitPathRe.exec(url);
        const videoType = pathParts ? pathParts.pop().toLowerCase() : "mp4";

        options.sources = [{ type: `${media.type}/${videoType}`, src: url }];
    } else {
        options.sources = [{ type: `${media.type}/${provider}`, src: url }];
    }
    options = shared._getOptionsPoster({ options, poster, container, media, filestack });

    const videoPlayer = videojs(el, options);
    shared.videoStack[id] = videoPlayer;

    shared._extraClasses(videoPlayer, isEdit, fromFroala);

    if (videoPlayer.bigPlayButton) {
        const handleBigPlayBtnClick = () => {
            if (videoPlayer.hasStarted() && !videoPlayer.hasClass("vjs-paused")) {
                videoPlayer.pause();
            }
        };
        videoPlayer.bigPlayButton.on("click", handleBigPlayBtnClick);
        videoPlayer.bigPlayButton.on("touchend", handleBigPlayBtnClick);
    }

    videoPlayer.on("ready", function() {
        $(videoPlayer.el_).attr("data-media-id", media.id);
        const captionButton = $(videoPlayer.el_).find(".vjs-captions-button");
        if (a11y && a11y.captions) {
            const caption = getCaption(a11y);
            if (!caption || !caption.src) {
                $(captionButton).addClass("hide-caption-button");
                return;
            }
            updateTextTrack(videoPlayer, a11y);
            videoPlayer.textTracks().on("change", () => {
                if ($(videoPlayer.el_).find(".vjs-selected .vjs-menu-item-text")[0].textContent === "captions off") {
                    $(captionButton).removeClass("show-closed-caption");
                } else {
                    $(captionButton).addClass("show-closed-caption");
                }
            });
            if (!caption || !caption.src) {
                $(captionButton).addClass("hide-caption-button");
            }
        } else {
            $(captionButton).addClass("hide-caption-button");
        }
        toggleIPhoneCSS(videoPlayer, provider);
    });

    videoPlayer.on("play", function() {
        shared.videoStack[id] = this;
        shared._pauseMedias(id);
    });

    videoPlayer.on("ended", function() {
        videoPlayer.addClass("vjs-paused");
        videoPlayer.removeClass("vjs-playing");
        onProgress.call(null, videoPlayer, "ended");
    });

    videoPlayer.on("fullscreenchange", () => {
        if (container.hasClass("kiom-widget-container")) {
            container.parent().toggleClass("kds-build-widget");
        }

        if (onFullscreenChange) {
            onFullscreenChange.call(null, videoPlayer);
        }
    });

    videoPlayer.on("useractive", () => {
        toggleOverlayVisibility(videoPlayer);
    });

    videoPlayer.on("seeked", () => {
        onProgress.call(null, videoPlayer, "seeked");
    });

    videoPlayer.on("pause", () => {
        onProgress.call(null, videoPlayer, "pause");
    });

    videoPlayer.on("userinactive", () => {
        toggleOverlayVisibility(videoPlayer);
    });

    /* For iframe resizers event on load: IE, Firefox */
    if (!videoPlayer.resizeManager.ResizeObserver) {
        shared._playerResize(videoPlayer, videoResize);
        toggleIPhoneCSS(videoPlayer, provider);
    }

    videoPlayer.on("playerresize", () => {
        shared._playerResize(videoPlayer, videoResize);
        toggleIPhoneCSS(videoPlayer, provider);
    });

    shared._initMarkers(videoPlayer, settings.markersOptions);
    videoPlayer.a11y();

    $el.find("button.vjs-fullscreen-control").on("click", e => {
        if ($(e.currentTarget).attr("title") === "Fullscreen") {
            $("#intercom-container").hide();
        } else {
            $("#intercom-container").show();
        }
    });

    return videoPlayer;
};
function getCaption(a11y) {
    const caption = a11y.captions.find(function(element) {
        return element.srcLang === "en";
    });
    return caption;
}
function getTextTrack(caption) {
    const trackAttrs = {
        src: caption.src,
        kind: "captions",
        srclang: caption.srcLang,
        label: caption.label,
    };
    return trackAttrs;
}
function updateTextTrack(videoPlayer, a11y) {
    const textTrack = getTextTrack(getCaption(a11y));
    const tracks = Array.from(videoPlayer.textTracks());
    for (const track of tracks) {
        videoPlayer.removeRemoteTextTrack(track);
    }
    videoPlayer.addRemoteTextTrack(textTrack, true);
}
function toggleIPhoneCSS(videoPlayer, provider) {
    const iPhone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
    const $videoPlayer = $(videoPlayer.el_);
    const cssClasses = "vjs-youtube-mobile iPhone";
    let $overlay = $videoPlayer
        .parent()
        .parent()
        .find(".iphone-video-player-overlay");
    const overlayTouched = () => {
        videoPlayer.userActive(true);
        toggleOverlayVisibility(videoPlayer);
        clearTimeout(videoPlayer.options.inactivityTimeout);
        videoPlayer.options.inactivityTimeout = setTimeout(function() {
            videoPlayer.userActive(false);
        }, 5000);
    };
    if (iPhone && provider === "youtube") {
        $videoPlayer.addClass(cssClasses);

        //create an overlay to capture the touchend event
        if ($overlay.length === 0) {
            $overlay = $("<div></div>")
                .addClass("iphone-video-player-overlay")
                .css({
                    "width": "100%",
                    "height": "100%",
                    "position": "absolute",
                    "top": 0,
                    "left": 0,
                    "z-index": 9,
                    "-webkit-overflow-scrolling": "touch",
                });
            $overlay[0].addEventListener("touchend", overlayTouched);
            $videoPlayer
                .parent()
                .parent()
                .append($overlay);
        }
    } else {
        $videoPlayer.removeClass(cssClasses);
        if ($overlay.length > 0) {
            $overlay[0].removeEventListener("touchend", overlayTouched);
            $overlay.remove();
        }
    }
}
function toggleOverlayVisibility(videoPlayer) {
    const $videoPlayer = $(videoPlayer.el_);
    let $overlay = $videoPlayer
        .parent()
        .parent()
        .find(".iphone-video-player-overlay");
    $videoPlayer.hasClass("vjs-user-active") ? $overlay.hide() : $overlay.show();
}
